import React, { ReactElement, useEffect } from "react";
import {
  Theme,
  CircularProgress,
  List,
  ListItemText,
  Divider,
  ListItem,
  Paper,
} from "@material-ui/core";
import { makeStyles, ThemeProvider, createMuiTheme } from "@material-ui/core/styles";
import { BrowserRouter, NavLink, Route, Switch } from "react-router-dom";
import { MenuBar, Snackbar } from "./components";
import { useAuth } from "./contexts/AuthContext";
import { Home, Docs, Login } from "./routes";
import Case from "./routes/Case";
import { ViProvider } from "./contexts/ViContext";
import { useErrorContext } from "./contexts/ErrorContext";
import { useApp } from "./contexts/AppContext";
import { one as themeConfig } from "./ui/ViTheme";
import { PartsterProvider } from "./contexts/PartsterContext";
import { version } from "../package.json";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    [theme.breakpoints.down("sm")]: {
      minHeight: "100%",
    },
    height: "100vh",
    width: "100vw",
    position: "relative",
    display: "flex",
    flexFlow: "column nowrap",
    "& header": {
      position: "relative",
      zIndex: 1,
    },
  },
  loader: {
    margin: "auto",
  },
  smallPrint: {
    position: "absolute",
    bottom: 0,
    left: 0,
    userSelect: "none",
    margin: `${theme.goldenRatio(-3)}rem`,
    fontSize: "0.618rem",
    height: "0.8rem",
  },
  copyright: {
    ...theme.typography.caption,
  },
  version: {
    ...theme.typography.caption,
    color: theme.palette.grey[600],
    marginLeft: `${theme.goldenRatio(-3)}rem`,
  },
  mainWrapper: {
    display: "flex",
    height: `calc(100% - ${theme.goldenRatio(3)}rem)`,
    maxWidth: "100vw",
  },
  main: { minWidth: "100vw", boxShadow: theme.shadows[4] },
  drawer: {
    width: "15rem",
  },
  drawerWrapper: ({ isDrawerOpen }: { isDrawerOpen: boolean }) => ({
    zIndex: 0,
    position: "relative",
    flexShrink: 0,
    overflowX: "hidden",
    transition: "width 0.382s ease-out",
    ...(isDrawerOpen ? { width: "15rem" } : { width: 0 }),
  }),
}));

const darkTheme = createMuiTheme({
  damageTypes: themeConfig.damageTypes,
  palette: { type: "dark" },
});

function App(): ReactElement {
  const AuthContext = useAuth();
  const ErrorContext = useErrorContext();
  const { isLoading, isAuthenticated, loginWithRedirect, user } = AuthContext;
  const {
    warnings,
    error,
    closeError,
    closeWarnings,
    isDrawerOpen,
    toggleDrawer,
  } = useApp();
  const classes = useStyles({ isDrawerOpen });

  useEffect(() => {
    if (isAuthenticated) {
      user?.email && ErrorContext.setUser(user?.email);
    }
  }, [AuthContext, isAuthenticated, ErrorContext, user]);

  return (
    <BrowserRouter>
      <div className={classes.container}>
        {isLoading ? (
          <CircularProgress color="secondary" size="5rem" className={classes.loader} />
        ) : isAuthenticated ? (
          <ViProvider {...{ AuthContext }}>
            <>
              <MenuBar />
              {<Snackbar {...{ error, warnings, closeError, closeWarnings }}></Snackbar>}
              <div className={classes.mainWrapper}>
                <ThemeProvider theme={darkTheme}>
                  <Paper
                    square
                    elevation={0}
                    className={classes.drawerWrapper}
                    onClick={() => toggleDrawer && toggleDrawer()}
                  >
                    <List className={classes.drawer}>
                      <ListItem
                        button
                        key="home"
                        component={NavLink}
                        to={"/"}
                        activeClassName={"Mui-selected"}
                        exact
                      >
                        <ListItemText primary={"Home"} />
                      </ListItem>
                      <ListItem
                        button
                        key="partster"
                        component={NavLink}
                        to={"/partster"}
                        activeClassName={"Mui-selected"}
                        disabled
                        exact
                      >
                        <ListItemText
                          primary={"Partster Playground"}
                          secondary={"Coming Soon"}
                        />
                      </ListItem>
                      <ListItem
                        button
                        key={"vi-core-docs"}
                        component={NavLink}
                        to={"/docs"}
                        activeClassName={"Mui-selected"}
                      >
                        <ListItemText primary={"VI Core Docs"} />
                      </ListItem>
                    </List>
                    <Divider />
                    <div className={classes.smallPrint}>
                      <small className={classes.copyright}>© Solera 2020</small>
                      <small className={classes.version}>{`v${version}`}</small>
                    </div>
                  </Paper>
                </ThemeProvider>
                <main className={classes.main}>
                  <PartsterProvider>
                    <Switch>
                      <Route path={"/vi"} component={Case} />
                      <Route exact path={"/docs"} component={Docs} />
                      <Route component={Home} />
                    </Switch>
                  </PartsterProvider>
                </main>
              </div>
            </>
          </ViProvider>
        ) : (
          <Login
            onLogin={() =>
              loginWithRedirect({
                appState: { returnTo: window.location.pathname },
              })
            }
          />
        )}
      </div>
    </BrowserRouter>
  );
}

export default App;
